import { Text } from '@airwallex/typography';
import React from 'react';
import { getAddressLine2 } from '@/utils/address';
import { getCountryName } from '@/utils/country';
import { getCustomerDisplayName } from '@/utils/customer';
import i18n from '@/utils/i18n';
import type { Customer, LegalEntity } from '@/components/Invoice/types';

// Field mapping mirrors billing-pdf-generator's
// `src/service/invoiceHtmlRenderer/index.ts` (the PDF is the source of truth),
// so the hosted invoice page renders the bill-from / bill-to block identically.
//
// Layout mirrors the Figma node 9589:163282 ("From"):
//   - 4px vertical gap between the name, the address block, email, phone, taxId
//   - the three address rows (line 1, line 2, country) are one tight block
//     where the only inter-line spacing is the hint line-height
//   - name renders with body-emphasis (medium weight); other rows are hint regular

type BillingFieldsProps =
  | { type: 'seller'; data: LegalEntity | null | undefined }
  | { type: 'customer'; data: Customer | null | undefined };

type ResolvedFields = {
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
  country?: string;
  email?: string;
  phoneNumber?: string;
  taxId?: string;
};

const fromSeller = (seller: LegalEntity | null | undefined): ResolvedFields => ({
  name: seller?.name,
  addressLine1: seller?.street,
  addressLine2: getAddressLine2(seller?.city, seller?.state, seller?.postcode) || undefined,
  country: getCountryName(seller?.countryCode, i18n.language) || undefined,
  email: seller?.email,
  phoneNumber: seller?.phoneNumber,
  taxId: seller?.taxId,
});

const fromCustomer = (customer: Customer | null | undefined): ResolvedFields => {
  const address = customer?.address;
  return {
    name: getCustomerDisplayName(customer ?? undefined),
    addressLine1: address?.street,
    addressLine2: getAddressLine2(address?.city, address?.state, address?.postcode) || undefined,
    country: getCountryName(address?.countryCode, i18n.language) || undefined,
    email: customer?.email,
    phoneNumber: customer?.phoneNumber,
    taxId: customer?.taxId,
  };
};

const wrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  alignItems: 'flex-start',
  width: '100%',
};

const nameStyle: React.CSSProperties = { fontWeight: 500 };

const BillingFields: React.FC<BillingFieldsProps> = (props) => {
  const { name, addressLine1, addressLine2, country, email, phoneNumber, taxId } =
    props.type === 'seller' ? fromSeller(props.data) : fromCustomer(props.data);

  const hasAddressBlock = !!(addressLine1 || addressLine2 || country);
  const hasAnyContent = !!(name || hasAddressBlock || email || phoneNumber || taxId);

  if (!hasAnyContent) return null;

  return (
    <div data-test="billing-fields" style={wrapperStyle}>
      {name && (
        <Text variant="body" style={nameStyle}>
          {name}
        </Text>
      )}
      {hasAddressBlock && (
        <div data-test="billing-address-block">
          {addressLine1 && <Text variant="hint">{addressLine1}</Text>}
          {addressLine2 && <Text variant="hint">{addressLine2}</Text>}
          {country && <Text variant="hint">{country}</Text>}
        </div>
      )}
      {email && <Text variant="hint">{email}</Text>}
      {phoneNumber && <Text variant="hint">{phoneNumber}</Text>}
      {taxId && <Text variant="hint">{taxId}</Text>}
    </div>
  );
};

export default BillingFields;
